import { useMemo, useState } from "react";
import { Copy, Check, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";
import { NICHES } from "../lib/niches";
import { PUBLIC_BASE } from "../lib/publicBase";

/**
 * AdminLandingPages
 *
 * One-screen index of every programmatic niche landing page Cauldron
 * ships. Shows the canonical production URL (always `thecauldron.app`,
 * never the preview), the niche label, copy-to-clipboard, and a one-click
 * "open" link for the operator to verify the live page.
 *
 * Why surface this in admin:
 *   The operator pastes these URLs into IG bios, paid ads, partnership
 *   emails and outreach DMs. Having all 17 in one place — with copy
 *   buttons — saves the operator from grepping the codebase or
 *   memorizing the slug format every time they need a link.
 *
 * Sort + search are debounce-free because 17 items is fast.
 */
export default function AdminLandingPages() {
  const [q, setQ] = useState("");
  const [copiedSlug, setCopiedSlug] = useState(null);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    // The seeker landing isn't a "niche" but operators paste it
    // alongside niche links in IG bios and ads — surface it first.
    const all = [
      { slug: "clients", label: "For Clients (Seekers)", url: `${PUBLIC_BASE}/for/clients`, audience: "Seekers" },
      ...NICHES.map((n) => ({
        slug: n.slug,
        label: n.label,
        url: `${PUBLIC_BASE}/for/${n.slug}`,
        audience: "Practitioners",
      })),
    ];
    return all.filter((r) =>
      !needle || r.label.toLowerCase().includes(needle) || r.slug.includes(needle)
    );
  }, [q]);

  const copy = async (url, slug) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedSlug((s) => (s === slug ? null : s)), 1500);
    } catch {
      toast.error("Couldn't copy — try long-press");
    }
  };

  const copyAll = async () => {
    const text = rows.map((r) => `${r.label}: ${r.url}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${rows.length} URLs`);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-landing-pages">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-serif text-xl">Landing Pages</h2>
          <p className="text-sm text-foreground/60 mt-1">
            {NICHES.length + 1} SEO-optimized landing pages — one for seekers and {NICHES.length} per spiritual segment.
            Paste these into IG bios, paid ads, or outreach DMs.
          </p>
        </div>
        <button
          onClick={copyAll}
          className="text-xs uppercase tracking-[0.18em] border border-secondary/40 text-secondary px-4 py-2 rounded-sm hover:bg-secondary/10 inline-flex items-center gap-2"
          data-testid="copy-all-landing-urls"
        >
          <Copy size={12} /> Copy all
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter (e.g. babalawo)…"
          className="w-full bg-background border border-secondary/20 rounded-sm pl-9 pr-3 py-2 text-sm"
          data-testid="landing-search-input"
        />
      </div>

      {/* Table */}
      <div className="border border-secondary/15 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/5 text-foreground/55 text-[10px] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left px-4 py-2 w-[22%]">Page</th>
              <th className="text-left px-4 py-2 w-[14%]">Audience</th>
              <th className="text-left px-4 py-2">URL</th>
              <th className="text-right px-4 py-2 w-[15%]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.slug}
                className="border-t border-secondary/10 hover:bg-secondary/[0.03]"
                data-testid={`landing-row-${r.slug}`}
              >
                <td className="px-4 py-2.5 font-medium">{r.label}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded ${r.audience === "Seekers" ? "bg-emerald-500/15 text-emerald-300" : "bg-secondary/15 text-secondary"}`}>
                    {r.audience}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:underline inline-flex items-center gap-1.5 font-mono text-xs"
                    data-testid={`landing-link-${r.slug}`}
                  >
                    {r.url}
                    <ExternalLink size={11} />
                  </a>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => copy(r.url, r.slug)}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] text-foreground/70 hover:text-secondary px-2 py-1 rounded"
                    data-testid={`copy-landing-${r.slug}`}
                    aria-label={`Copy ${r.label} landing URL`}
                  >
                    {copiedSlug === r.slug ? (
                      <>
                        <Check size={12} className="text-emerald-400" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy size={12} /> Copy
                      </>
                    )}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-foreground/45">
                  No pages match &ldquo;{q}&rdquo;
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / SEO note */}
      <div className="text-xs text-foreground/45 leading-relaxed">
        All 17 URLs are indexed in{" "}
        <a
          href={`${PUBLIC_BASE}/api/sitemap-niches.xml`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary hover:underline"
        >
          /api/sitemap-niches.xml
        </a>{" "}
        and referenced from the master sitemap. Updates to copy or imagery
        flow through a single component (<code>NicheLanding.jsx</code>) plus
        the niche data table (<code>lib/niches.js</code>) — no per-page maintenance required.
      </div>
    </div>
  );
}
